import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { courseById, BATCH_STATUSES, type BatchStatus, type StudentStatus } from "@mizpah/shared";
import { api, AdminError } from "../adminClient.js";
import { badgeClass } from "../badge.js";

interface Batch {
  batchId: string; courseId: string; name: string;
  startDate: string; endDate: string; capacity: number; status: BatchStatus;
}
interface RosterStudent {
  studentId: string; fullName: string; status: StudentStatus;
  idph?: { status: string }; payment?: { status: string };
}

export default function BatchDetailPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const [batch, setBatch] = useState<Batch | null>(null);
  const [roster, setRoster] = useState<RosterStudent[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    api<{ batch: Batch; roster: RosterStudent[] }>(`/admin/batches/${id}`)
      .then((d) => { setBatch(d.batch); setRoster(d.roster); })
      .catch((e) => setError(e instanceof AdminError ? e.message : "Failed to load batch"));
  }, [id]);
  useEffect(load, [load]);

  async function setStatus(status: string) {
    setBusy(true); setError(""); setSaved(false); setNote("");
    try {
      const res = await api<{ promoted?: number }>(`/admin/batches/${id}`, { method: "PATCH", body: { status } });
      load();
      if (res.promoted) setNote(`${res.promoted} student${res.promoted === 1 ? "" : "s"} moved to In Progress.`);
      setSaved(true); window.setTimeout(() => setSaved(false), 2500);
    }
    catch (e) { setError(e instanceof AdminError ? e.message : "Update failed"); }
    finally { setBusy(false); }
  }

  if (error && !batch) return <div className="login-err">{error}</div>;
  if (!batch) return <p className="subhead">Loading...</p>;

  const full = roster.length >= batch.capacity;
  // Withdrawn students stay on the roster (records are never deleted), so group
  // them below the active ones instead of interleaving them alphabetically.
  const active = roster.filter((s) => s.status !== "Withdrawn");
  const withdrawn = roster.filter((s) => s.status === "Withdrawn");

  return (
    <>
      <p className="subhead"><Link to="/admin/batches">← All batches</Link></p>
      {error && <div className="login-err" style={{ marginBottom: 12 }}>{error}</div>}
      <div className="detail">
        <div className="card pad">
          <div className="row">
            <h1 style={{ fontSize: 22 }}>{batch.name}</h1>
            <span className={badgeClass(batch.status)}>{batch.status}</span>
          </div>
          <dl className="kv">
            <dt>Program</dt><dd>{courseById(batch.courseId)?.name ?? batch.courseId}</dd>
            <dt>Dates</dt><dd>{batch.startDate} → {batch.endDate}</dd>
            <dt>Enrolled</dt>
            <dd>
              {roster.length} / {batch.capacity} {full && <span className="flag" style={{ marginLeft: 6 }}>full</span>}
              {withdrawn.length > 0 && (
                <span className="sub" style={{ marginLeft: 6 }}>
                  ({active.length} active · {withdrawn.length} withdrawn)
                </span>
              )}
            </dd>
          </dl>
          <Link to={`/admin/batches/${batch.batchId}/report`} className="rbtn coral" style={{ display: "inline-block", textDecoration: "none", marginBottom: 14 }}>
            View batch report →
          </Link>
          <div className="alabel">Batch status
            {busy && <span className="sub"> · Saving...</span>}
            {saved && <span className="sub savedcue"> · Saved ✓</span>}
          </div>
          <select className="ainput" aria-label="Batch status" value={batch.status} disabled={busy} onChange={(e) => setStatus(e.target.value)}>
            {BATCH_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <p className="sub" style={{ marginTop: 8, marginBottom: 0 }}>
            {note || "Activating a batch moves its enrolled students to In Progress."}
          </p>
        </div>

        <div className="card pad">
          <div className="alabel" style={{ marginTop: 0 }}>Roster</div>
          {roster.length === 0 ? (
            <p className="sub">No students enrolled yet. Enroll leads from the Inquiries screen.</p>
          ) : (
            <>
              <table>
                <thead><tr><th>Name</th><th>Status</th><th>Payment</th></tr></thead>
                <tbody>
                  {/* Group labels only appear when the roster is actually split -
                      an all-active roster needs no heading. */}
                  {withdrawn.length > 0 && (
                    <tr className="rgroup-head rgroup-head-first">
                      <td colSpan={3}>Active · {active.length}</td>
                    </tr>
                  )}
                  {active.map((s) => (
                    <tr key={s.studentId} onClick={() => nav(`/admin/students/${s.studentId}`)}>
                      <td>
                        <div className="nm">{s.fullName}</div>
                        {s.idph && <div className="sub">IDPH: {s.idph.status}</div>}
                      </td>
                      <td><span className={badgeClass(s.status)}>{s.status}</span></td>
                      <td>{s.payment ? <span className={badgeClass(s.payment.status)}>{s.payment.status}</span> : "-"}</td>
                    </tr>
                  ))}
                </tbody>
                {withdrawn.length > 0 && (
                  <tbody className="rgroup-out">
                    <tr className="rgroup-head">
                      <td colSpan={3}>Withdrawn · {withdrawn.length}</td>
                    </tr>
                    {withdrawn.map((s) => (
                      <tr key={s.studentId} onClick={() => nav(`/admin/students/${s.studentId}`)}>
                        <td>
                          <div className="nm">{s.fullName}</div>
                          {s.idph && <div className="sub">IDPH: {s.idph.status}</div>}
                        </td>
                        <td><span className={badgeClass(s.status)}>{s.status}</span></td>
                        <td>{s.payment ? <span className={badgeClass(s.payment.status)}>{s.payment.status}</span> : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
              <div className="cards-list">
                {withdrawn.length > 0 && (
                  <div className="rgroup-divider rgroup-divider-first">Active · {active.length}</div>
                )}
                {active.map((s) => (
                  <div key={s.studentId} className="card pad" style={{ marginBottom: 10, cursor: "pointer" }} onClick={() => nav(`/admin/students/${s.studentId}`)}>
                    <div className="row"><div className="nm">{s.fullName}</div><span className={badgeClass(s.status)}>{s.status}</span></div>
                    <div className="sub" style={{ marginTop: 4 }}>{s.payment ? `Payment: ${s.payment.status}` : ""}{s.idph ? ` · IDPH: ${s.idph.status}` : ""}</div>
                  </div>
                ))}
                {withdrawn.length > 0 && (
                  <>
                    <div className="rgroup-divider">Withdrawn · {withdrawn.length}</div>
                    {withdrawn.map((s) => (
                      <div key={s.studentId} className="card pad rcard-out" style={{ marginBottom: 10, cursor: "pointer" }} onClick={() => nav(`/admin/students/${s.studentId}`)}>
                        <div className="row"><div className="nm">{s.fullName}</div><span className={badgeClass(s.status)}>{s.status}</span></div>
                        <div className="sub" style={{ marginTop: 4 }}>{s.payment ? `Payment: ${s.payment.status}` : ""}{s.idph ? ` · IDPH: ${s.idph.status}` : ""}</div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </>
          )}
        </div>

      </div>
    </>
  );
}
